/* The Org vault — API-key + org-chart lookup test. */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Link2, CheckCircle2, AlertTriangle, Loader2, Eye, EyeOff, Search } from "lucide-react";
import { SubSection } from "./VaultBits";

/* ----------------------------------------------------------------- */
/*  The Org vault — api_key + embed_key + usage + company lookup     */
/* ----------------------------------------------------------------- */
function TheOrgVault({ cfg, onSaved }) {
    const { t } = useTranslation();
    const settings = cfg.settings || {};
    const [apiKey, setApiKey] = useState("");
    const [embedKey, setEmbedKey] = useState("");
    const [reveal, setReveal] = useState(false);
    const [revealEmbed, setRevealEmbed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [usageBusy, setUsageBusy] = useState(false);
    const [usage, setUsage] = useState(null);
    const embedConfigured = Boolean(settings.embed_key_masked);
    const [lookupDomain, setLookupDomain] = useState("");
    const [lookupBusy, setLookupBusy] = useState(false);
    const [lookup, setLookup] = useState(null);

    const saveKey = async () => {
        if (!apiKey.trim() && !cfg.configured) { toast.error(t("integr.enter_key")); return; }
        setBusy(true);
        try {
            await api.patch("/integrations/theorg", { api_key: apiKey });
            toast.success(t("integr.key_saved", { name: "The Org" }));
            setApiKey(""); setReveal(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const saveEmbed = async () => {
        if (!embedKey.trim()) { toast.error(t("integr.enter_embed_key")); return; }
        setBusy(true);
        try {
            await api.patch("/integrations/theorg", {
                settings: { ...settings, embed_key: embedKey, embed_key_masked: "•".repeat(Math.max(0, embedKey.length - 4)) + embedKey.slice(-4) },
            });
            toast.success(t("integr.embed_saved"));
            setEmbedKey(""); setRevealEmbed(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t("integr.theorg_delete_confirm"))) return;
        setBusy(true);
        try { await api.delete("/integrations/theorg"); toast.success(t("integr.deleted")); setUsage(null); setLookup(null); onSaved(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("integr.delete_failed")); }
        finally { setBusy(false); }
    };
    const testUsage = async () => {
        setUsageBusy(true);
        try {
            const r = await api.get("/integrations/theorg/usage");
            setUsage(r.data || {});
            toast.success(t("integr.theorg_valid"));
        } catch (e) { setUsage(null); toast.error(e?.response?.data?.detail || t("integr.test_failed")); }
        finally { setUsageBusy(false); }
    };
    const runLookup = async () => {
        if (!lookupDomain.trim()) { toast.error(t("integr.enter_domain")); return; }
        setLookupBusy(true);
        try {
            const r = await api.get(`/integrations/theorg/org-chart`, { params: { domain: lookupDomain.trim() } });
            setLookup(r.data);
            toast.success(t("integr.orgchart_loaded"));
        } catch (e) { setLookup(null); toast.error(e?.response?.data?.detail || t("integr.lookup_failed")); }
        finally { setLookupBusy(false); }
    };

    return (
        <div className="space-y-4">
            <SubSection title={`1. ${t("integr.sec_credentials")}`} testid="theorg-credentials">
                <div className="grid sm:grid-cols-2 gap-3">
                    {/* API Key */}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">API Key (X-Api-Key)</label>
                        <div className="mt-1 relative">
                            <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                            <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                                placeholder={cfg.api_key_masked || t("integr.paste_key")}
                                type={reveal ? "text" : "password"}
                                data-testid="theorg-apikey-input"
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                            <button type="button" onClick={() => setReveal((r) => !r)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--jr-text-3)] hover:text-white">
                                {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                        <button onClick={saveKey} disabled={busy || !apiKey.trim()} data-testid="theorg-save-apikey"
                            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} {t("common.save")}
                        </button>
                    </div>
                    {/* Embed Key */}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Embed Key (publicKey)</label>
                        <div className="mt-1 relative">
                            <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                            <input value={embedKey} onChange={(e) => setEmbedKey(e.target.value)}
                                placeholder={settings.embed_key_masked || t("integr.paste_embed_key")}
                                type={revealEmbed ? "text" : "password"}
                                data-testid="theorg-embedkey-input"
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                            <button type="button" onClick={() => setRevealEmbed((r) => !r)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--jr-text-3)] hover:text-white">
                                {revealEmbed ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                        <button onClick={saveEmbed} disabled={busy || !embedKey.trim()} data-testid="theorg-save-embedkey"
                            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} {t("common.save")}
                        </button>
                        {embedConfigured && (
                            <span className="ml-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[#22c55e]">
                                ✓ {t("integr.embed_active")}
                            </span>
                        )}
                    </div>
                </div>
                <p className="mt-3 text-[10px] text-[var(--jr-text-3)]">
                    {t("integr.theorg_where")} <span className="text-[var(--jr-text-2)]">Manage subscription</span> → Developers / Embed Keys.
                </p>
                {cfg.configured && (
                    <button onClick={remove} className="mt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-[#ef4444]">
                        {t("integr.delete_config")}
                    </button>
                )}
            </SubSection>

            <SubSection title={`2. ${t("integr.sec_test_conn")}`}
                action={
                    <button onClick={testUsage} disabled={usageBusy || !cfg.configured} data-testid="theorg-test"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25 disabled:opacity-50">
                        {usageBusy ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />} {t("integr.test_usage")}
                    </button>
                }
                testid="theorg-test-section">
                {!usage ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.theorg_test_hint")}</p>
                ) : (
                    <div className="rounded-lg border border-[#22c55e]/25 bg-[#22c55e]/[0.06] p-3 text-xs text-[var(--jr-text-2)]"
                        data-testid="theorg-usage-result">
                        <div className="inline-flex items-center gap-2 text-white">
                            <CheckCircle2 size={14} className="text-[#22c55e]" /> {t("integr.conn_ok")}
                        </div>
                        <pre className="mt-2 text-[10px] font-mono text-[var(--jr-text-3)] whitespace-pre-wrap leading-snug">
{JSON.stringify(usage.usage || usage, null, 2).slice(0, 600)}
                        </pre>
                        <div className="mt-1 text-[10px] text-[var(--jr-text-3)]">
                            Embed Key: {usage.embed_key_configured ? `✓ ${t("integr.embed_active")}` : `✗ ${t("integr.not_configured").toLowerCase()}`}
                        </div>
                    </div>
                )}
            </SubSection>

            <SubSection title={`3. ${t("integr.sec_company_lookup")}`} testid="theorg-lookup-section">
                <div className="flex gap-2">
                    <div className="flex-1 relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                        <input value={lookupDomain} onChange={(e) => setLookupDomain(e.target.value)}
                            placeholder={t("integr.domain_ph")}
                            data-testid="theorg-lookup-input"
                            onKeyDown={(e) => e.key === "Enter" && runLookup()}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </div>
                    <button onClick={runLookup} disabled={lookupBusy || !cfg.configured} data-testid="theorg-lookup-run"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {lookupBusy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Lookup
                    </button>
                </div>
                {lookup && (
                    <div className="mt-3 rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 p-3 space-y-2 text-xs text-[var(--jr-text-2)]"
                        data-testid="theorg-lookup-result">
                        <div className="flex items-center justify-between">
                            <span className="text-white font-medium">{lookup.company_key}</span>
                            <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                                {lookup.position_count} {t("integr.positions")} · {lookup.departments?.length || 0} {t("integr.departments")}
                            </span>
                        </div>
                        {lookup.api_warning && (
                            <div className="inline-flex items-start gap-2 text-[11px] text-[var(--jr-amber)] bg-[var(--jr-amber)]/[0.08] border border-[var(--jr-amber)]/25 rounded-lg px-2.5 py-1.5">
                                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                <div><strong className="text-white">API quota:</strong> {lookup.api_warning}. {t("integr.embed_keeps_working")}</div>
                            </div>
                        )}
                        {lookup.departments?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {lookup.departments.slice(0, 12).map((d) => (
                                    <span key={d} className="text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/25">
                                        {d}
                                    </span>
                                ))}
                            </div>
                        )}
                        {lookup.embed_url && (
                            <details className="mt-2" open={!!lookup.api_warning}>
                                <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-white">
                                    {t("integr.show_orgchart")}
                                </summary>
                                <iframe src={lookup.embed_url} title="Org chart"
                                    className="mt-2 w-full h-[480px] border-0 rounded-lg bg-white"
                                    data-testid="theorg-embed-iframe" />
                            </details>
                        )}
                    </div>
                )}
            </SubSection>
        </div>
    );
}

export default TheOrgVault;
